<template>
  <div class="flex items-center gap-2 flex-wrap">
    <!-- Interview Stage Filter -->
    <Select
      v-if="showInterviewStatus"
      :model-value="selectedInterviewStatus"
      @update:model-value="handleInterviewStatusChange"
    >
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="selectedInterviewStatus !== 'all'
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <Clock class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Interview Stage" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value="all">
            <span class="text-xs">All Interview Stages</span>
          </SelectItem>
          <SelectItem
            v-for="option in interviewStatusOptions"
            :key="option.value"
            :value="option.value"
          >
            <span
              :class="[option.bgColor, option.textColor]"
              class="px-2 py-0.5 rounded-full text-xs"
            >
              {{ option.label }}
            </span>
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>

    <!-- Application Status Filter -->
    <Select
      v-if="showApplicationStatus"
      :model-value="selectedApplicationStatus"
      @update:model-value="handleApplicationStatusChange"
    >
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="selectedApplicationStatus !== 'all'
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <Flag class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Application Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value="all">
            <span class="text-xs">All Application Statuses</span>
          </SelectItem>
          <SelectItem
            v-for="option in applicationStatusOptions"
            :key="option.value"
            :value="option.value"
          >
            <span
              :class="[option.bgColor, option.textColor]"
              class="px-2 py-0.5 rounded-full text-xs"
            >
              {{ option.label }}
            </span>
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>

    <!-- Referral Status Filter -->
    <Select
      v-if="showReferralStatus"
      :model-value="selectedReferralStatus"
      @update:model-value="handleReferralStatusChange"
    >
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="selectedReferralStatus !== 'all'
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <Send class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Referral Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value="all">
            <span class="text-xs">All Referral Statuses</span>
          </SelectItem>
          <SelectItem
            v-for="option in referralStatusOptions"
            :key="option.value"
            :value="option.value"
          >
            <span
              :class="[option.bgColor, option.textColor]"
              class="px-2 py-0.5 rounded-full text-xs"
            >
              {{ option.label }}
            </span>
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>

    <!-- Referral Type Filter -->
    <Select
      v-if="showReferralType"
      :model-value="selectedReferralType"
      @update:model-value="handleReferralTypeChange"
    >
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="selectedReferralType !== 'all'
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <Handshake class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Referral Type" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value="all">
            <span class="text-xs">All Referral Types</span>
          </SelectItem>
          <SelectItem
            v-for="option in referralTypeOptions"
            :key="option.value"
            :value="option.value"
          >
            <span
              :class="[option.bgColor, option.textColor]"
              class="px-2 py-0.5 rounded-full text-xs"
            >
              {{ option.label }}
            </span>
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>

    <!-- Clear all -->
    <button
      v-if="isAnyActive"
      @click="handleClearAll"
      class="inline-flex items-center gap-1 h-8 px-3 text-xs rounded-full border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-500 transition-colors"
    >
      <X class="w-3 h-3" />
      Clear all
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Clock, Flag, Send, Handshake, X } from 'lucide-vue-next'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  applicationStatusOptions,
  interviewStatusOptions,
  referralStatusOptions,
  referralTypeOptions
} from '@/const/applicationStatuses'

import type { AcceptableValue } from 'reka-ui'

interface Props {
  title?: string
  showApplicationStatus?: boolean
  showReferralStatus?: boolean
  showReferralType?: boolean
  showInterviewStatus?: boolean
  modelValue?: {
    applicationStatus?: string
    referralStatus?: string
    referralType?: string
    interviewStatus?: string
  }
  onUpdate?: (filters: {
    applicationStatus?: string
    referralStatus?: string
    referralType?: string
    interviewStatus?: string
  }) => void
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Job Application Filters',
  showApplicationStatus: true,
  showReferralStatus: false,
  showReferralType: false,
  showInterviewStatus: true,
  modelValue: () => ({})
})

const emit = defineEmits<{
  'update:modelValue': [
    value: {
      applicationStatus?: string
      referralStatus?: string
      referralType?: string
      interviewStatus?: string
    }
  ]
  'filter-change': [
    filters: {
      applicationStatus?: string
      referralStatus?: string
      referralType?: string
      interviewStatus?: string
    }
  ]
  'clear-all': []
}>()

const selectedApplicationStatus = computed(() => {
  return props.modelValue?.applicationStatus || 'all'
})

const selectedReferralStatus = computed(() => {
  return props.modelValue?.referralStatus || 'all'
})

const selectedReferralType = computed(() => {
  return props.modelValue?.referralType || 'all'
})

const selectedInterviewStatus = computed(() => {
  return props.modelValue?.interviewStatus || 'all'
})

const isAnyActive = computed(() =>
  selectedApplicationStatus.value !== 'all' ||
  selectedReferralStatus.value !== 'all' ||
  selectedReferralType.value !== 'all' ||
  selectedInterviewStatus.value !== 'all'
)

const handleApplicationStatusChange = (status: AcceptableValue) => {
  if (typeof status === 'string') {
    const normalizedStatus = status === 'all' ? '' : status
    const newFilters = {
      ...props.modelValue,
      applicationStatus: normalizedStatus
    }

    emit('update:modelValue', newFilters)
    emit('filter-change', newFilters)

    if (props.onUpdate) {
      props.onUpdate(newFilters)
    }
  }
}

const handleReferralStatusChange = (status: AcceptableValue) => {
  if (typeof status === 'string') {
    const normalizedStatus = status === 'all' ? '' : status
    const newFilters = {
      ...props.modelValue,
      referralStatus: normalizedStatus
    }

    emit('update:modelValue', newFilters)
    emit('filter-change', newFilters)

    if (props.onUpdate) {
      props.onUpdate(newFilters)
    }
  }
}

const handleReferralTypeChange = (type: AcceptableValue) => {
  if (typeof type === 'string') {
    const normalizedType = type === 'all' ? '' : type
    const newFilters = {
      ...props.modelValue,
      referralType: normalizedType
    }

    emit('update:modelValue', newFilters)
    emit('filter-change', newFilters)

    if (props.onUpdate) {
      props.onUpdate(newFilters)
    }
  }
}

const handleInterviewStatusChange = (status: AcceptableValue) => {
  if (typeof status === 'string') {
    const normalizedStatus = status === 'all' ? '' : status
    const newFilters = {
      ...props.modelValue,
      interviewStatus: normalizedStatus
    }

    emit('update:modelValue', newFilters)
    emit('filter-change', newFilters)

    if (props.onUpdate) {
      props.onUpdate(newFilters)
    }
  }
}

const handleClearAll = () => {
  const newFilters: {
    applicationStatus?: string
    referralStatus?: string
    referralType?: string
    interviewStatus?: string
  } = { ...props.modelValue }

  if (props.showApplicationStatus) newFilters.applicationStatus = ''
  if (props.showReferralStatus) newFilters.referralStatus = ''
  if (props.showReferralType) newFilters.referralType = ''
  if (props.showInterviewStatus) newFilters.interviewStatus = ''

  emit('update:modelValue', newFilters)
  emit('filter-change', newFilters)
  emit('clear-all')
  if (props.onUpdate) {
    props.onUpdate(newFilters)
  }
}
</script>
